import { Box, Collapse } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { To, useNavigate, useParams } from 'react-router-dom'
import { AppRoutesNavigation } from '../../app-routes'
import { CenteredSpinner } from '../../components/Spinner'
import { ICustomer } from '../../generated-types'
import { useGetUserStorageCustomers } from '../../api/customer'
import { useSidebarCtx } from '../../hooks/context-hooks'
import { GridView, SvgIconComponent } from '@mui/icons-material'
import { CustomerHeader } from 'src/components/sidemenu/CustomerHeader'
import classNames from 'classnames'
import { IconType } from 'react-icons'
import { CollapsedSideMenu } from 'src/components/sidemenu/CollapsedSideMenu'
import { IStorage } from 'src/generated-types/storage-types'
import StorageCard from 'src/containers/storage/StorageCard'

export interface IMenuItem {
  title: string
  Icon: SvgIconComponent | IconType
  isMinified: boolean
  onClick?: (e) => void
  activePath?: string
  disabled?: boolean
  className?: string
  path?: string
  count?: number
  countBgColor?: string
}

export const StorageSidebar = () => {
  const { setCollapsibleSidebarOpen } = useSidebarCtx()

  useEffect(() => {
    setCollapsibleSidebarOpen(true)
  }, [])

  return <Sidebar />
}

export const Sidebar: React.FC = () => {
  const { data, isLoading } = useGetUserStorageCustomers()
  const navigate = useNavigate()
  const [path, setPath] = useState<string>('')
  const { t } = useTranslation(['common', 'sidebar'])
  const { customerId, storageId } = useParams()
  const { collapsibleSidebarOpen, setCollapsibleSidebarOpen } = useSidebarCtx()

  const getOnClick = (e: { stopPropagation: () => void }, navigationPath: To) => {
    e.stopPropagation()
    navigate(navigationPath)
  }

  function GetMenuItems() {
    const menuItems: IMenuItem[] = [
      {
        title: t('dashboard', { ns: 'sidebar' }),
        Icon: GridView,
        isMinified: true,
        onClick: e => getOnClick(e, AppRoutesNavigation.storage.storage(customerId as EntityId, storageId as EntityId)),
      },
    ]

    return menuItems
  }

  useEffect(() => {
    if (path && path != '') {
      window.location.pathname != '/storages' ? navigate(window.location.pathname) : navigate(path)
    }
  }, [path])

  if (isLoading) return <CenteredSpinner />

  return (
    <>
      <Box sx={{ width: '84px' }} className={!collapsibleSidebarOpen ? 'block h-full' : 'hidden'}>
        <CollapsedSideMenu
          menuItems={GetMenuItems()}
          customer={data?.find(value => value.id == (customerId as EntityId))}
          setPath={setPath}
          setToggleSidebar={setCollapsibleSidebarOpen}
          collapsibleSidebarOpen={!collapsibleSidebarOpen}
        />
      </Box>

      <Box
        height={'calc(100vh - 100px)'}
        className={collapsibleSidebarOpen ? 'block overflow-x-hidden overflow-y-auto hide-scrollbar' : 'hidden h-0'}
        paddingTop={'5px'}
      >
        {data?.map(
          customer =>
            customer && (
              <React.Fragment key={customer.id}>
                <CustomersContainer customer={customer} />
              </React.Fragment>
            ),
        )}
      </Box>
    </>
  )
}

const CustomersContainer: React.FC<{
  customer: ICustomer
}> = ({ customer }) => {
  const { customerId, storageId } = useParams()
  const [open, setOpen] = useState(customer.id == (customerId as EntityId))

  useEffect(() => {
    setOpen(customer.id == (customerId as EntityId))
  }, [customerId])

  const selectedStorage = customer?.storages?.find(value => value.id == (storageId as EntityId))

  if (customer?.storages && selectedStorage) {
    const index = customer?.storages.indexOf(selectedStorage)
    customer?.storages.splice(index, 1)
    customer?.storages.unshift(selectedStorage)
  }

  const { t } = useTranslation('service-station')

  return (
    <Box className={open ? 'bg-opacity-10   rounded-lg my-1' : '  rounded-lg my-1'}>
      <Box key={customer.id}>
        <Box
          sx={{ margin: '0px 12px 20px 12px' }}
          onClick={() => {
            setOpen(!open)
            localStorage.setItem('currentCustomerId', customer.id.toString())
            if (customer.isAdminCustomer)
              localStorage.setItem('selectedCustomerAdminCustomerId', customer.id.toString())
          }}
        >
          <CustomerHeader
            hasAdminAccess={customer.isAdminCustomer}
            isLandingPage={false}
            {...customer}
            active={open}
            isServiceStation={true}
          />
        </Box>
      </Box>

      <Box>
        <Collapse in={open}>
          <>
            {customer?.storages?.length ? (
              customer?.storages?.map(storage => (
                <Box key={storage.id} className="pt-0 pb-0 pr-0">
                  <CustomerStorageContainer storage={storage} open={storage.id == (storageId as any)} />
                </Box>
              ))
            ) : (
              <Box className="font-semibold text-center text-gray-600 text-l">
                {t('thisCustomerHasNoServiceStations', { ns: 'service-station' })}
              </Box>
            )}
          </>
        </Collapse>
      </Box>
    </Box>
  )
}

const CustomerStorageContainer: React.FC<{
  storage: IStorage
  open: boolean
}> = ({ storage, open }) => {
  const navigate = useNavigate()
  return (
    <Box className={classNames('p-8-core cursor-pointer')}>
      <StorageCard
        storage={storage}
        active={open}
        onClick={() => {
          navigate(AppRoutesNavigation.storage.storage(storage.customerId, storage.id))
        }}
      />
    </Box>
  )
}
